/**
 * Mark Follow-Up Dialog — set/clear Follow-Up Recommended flags on a study.
 *
 * Fields mirror GWT FollowUpWindow:
 * - isFollowUp toggle
 * - reason, note, phone
 * - actionNeeded
 * - remindTime / remindUnits (reminder period)
 * - isRemindPhy / isRemindPatient (who to remind)
 */
import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography,
  FormControlLabel, Checkbox, MenuItem, Select, InputLabel, FormControl,
} from '@mui/material';
import { api } from '../../api/rpc';
import type { Study } from '../../api/types';
import { gwtStyles } from '../../config/theme';
import { pushError, pushSuccess } from '../../stores/notificationStore';

interface Props {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

const REMIND_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
  { value: 'years', label: 'Years' },
];

export const MarkFollowUpDialog = ({ open, study, onClose, onComplete }: Props) => {
  const [saving, setSaving] = useState(false);
  const [isFollowUp, setIsFollowUp] = useState(true);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [phone, setPhone] = useState('');
  const [actionNeeded, setActionNeeded] = useState(false);
  const [remindTime, setRemindTime] = useState<number | ''>('');
  const [remindUnits, setRemindUnits] = useState('days');
  const [isRemindPhy, setIsRemindPhy] = useState(false);
  const [isRemindPatient, setIsRemindPatient] = useState(false);

  const fu = study?.followUp;

  useEffect(() => {
    if (open) {
      setIsFollowUp(fu?.isFollowUp ?? true);
      setReason(fu?.reason ?? '');
      setNote(fu?.note ?? '');
      setPhone(fu?.phone ?? '');
      setActionNeeded(fu?.actionNeeded ?? false);
      setRemindTime(fu?.remindTime ?? '');
      setRemindUnits(fu?.remindUnits ?? 'days');
      setIsRemindPhy(fu?.isRemindPhy ?? false);
      setIsRemindPatient(fu?.isRemindPatient ?? false);
    }
  }, [open, fu]);

  const handleSubmit = async () => {
    if (!study) return;
    setSaving(true);
    try {
      await api.markFollowUp({
        objKey: study.objKey,
        isFollowUp,
        reason: reason || undefined,
        actionNeeded,
        note: note || undefined,
        phone: phone || undefined,
        remindTime: remindTime !== '' ? Number(remindTime) : undefined,
        remindUnits: remindTime !== '' ? remindUnits : undefined,
        isRemindPhy,
        isRemindPatient,
      });
      pushSuccess(isFollowUp ? 'Marked for Follow-Up' : 'Follow-Up flag cleared');
      onComplete();
      onClose();
    } catch (e) {
      pushError('Failed to mark follow-up: ' + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px', border: `1px solid ${gwtStyles.borderSubtle}`, backgroundColor: gwtStyles.popupBg } }}>
      <DialogTitle sx={{ fontSize: 14, fontWeight: 600, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: gwtStyles.wizardDetail }}>Follow-Up Recommended</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {study && (
          <Typography variant="body2" color="text.secondary">
            {study.niceName ?? study.name} — {study.acc}
          </Typography>
        )}

        <FormControlLabel
          control={<Checkbox checked={isFollowUp} onChange={(_, v) => setIsFollowUp(v)} />}
          label="Mark as Follow-Up Recommended"
        />

        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          multiline
          rows={2}
          fullWidth
        />

        <TextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
          multiline
          rows={2}
          fullWidth
        />

        <TextField
          label="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          size="small"
          fullWidth
        />

        <FormControlLabel
          control={<Checkbox checked={actionNeeded} onChange={(_, v) => setActionNeeded(v)} />}
          label="Action Needed"
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Reminder Time"
            type="number"
            value={remindTime}
            onChange={(e) => setRemindTime(e.target.value === '' ? '' : Number(e.target.value))}
            size="small"
            sx={{ flex: 1 }}
            slotProps={{ htmlInput: { min: 0 } }}
          />
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>Units</InputLabel>
            <Select
              value={remindUnits}
              onChange={(e) => setRemindUnits(e.target.value)}
              label="Units"
            >
              {REMIND_UNITS.map((u) => (
                <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <FormControlLabel
          control={<Checkbox checked={isRemindPhy} onChange={(_, v) => setIsRemindPhy(v)} />}
          label="Remind Physician"
        />
        <FormControlLabel
          control={<Checkbox checked={isRemindPatient} onChange={(_, v) => setIsRemindPatient(v)} />}
          label="Remind Patient"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
